import { useQueryClient } from "@tanstack/react-query";
import { Formik, type FormikHelpers, useFormikContext } from "formik";
import { Plus } from "lucide-react";
import { useCallback, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import {
  type CreatePatient as ApiCreatePatient,
  useCreatePatient,
} from "@elation/api-client/src/stitch-web";
import { CreatePatientSex } from "@elation/api-client/src/stitch-web/models/createPatientSex";

import { PRODUCT_NAME } from "@src/shared/brand";
import { Alert, AlertDescription } from "@src/shared/components/ui/alert";
import { Button } from "@src/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/shared/components/ui/dialog";
import { Input } from "@src/shared/components/ui/input";
import { Label } from "@src/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/shared/components/ui/select";
import { Spinner } from "@src/shared/components/ui/spinner";
import { navigationV2 } from "@src/shared/navigation/v2";
import { useActor } from "@src/shared/providers/actorProvider";
import { getDisplayErrorMessage } from "@src/shared/utils/errors";
import { captureException } from "@src/shared/utils/observability/sentry";
import { NaiveDate } from "@src/shared/utils/types";

import {
  type CreatePatient,
  CreatePatientFormInitialValues,
  CreatePatientFormValidationSchema,
  type CreatePatientFormValues,
  createPatientFromFormValues,
} from "../../../../domain";

export interface CreatePatientDialogV2Props {
  canCreatePatient?: boolean;
  defaultOpen?: boolean;
}

const sexOptions = [
  { label: "Female", value: CreatePatientSex.Female },
  { label: "Male", value: CreatePatientSex.Male },
  { label: "Other", value: CreatePatientSex.Other },
  { label: "Unknown", value: CreatePatientSex.Unknown },
];

export function CreatePatientDialogV2({
  canCreatePatient = true,
  defaultOpen = false,
}: CreatePatientDialogV2Props) {
  const actor = useActor();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createPatient = useCreatePatient();
  const [open, setOpen] = useState(defaultOpen);
  const isSubmitting = createPatient.isPending;
  const error = createPatient.isError
    ? getDisplayErrorMessage(createPatient.error, {
        fallback: "Unable to create patient. Please try again.",
      })
    : null;
  const generatedId = useId();
  const titleId = `${generatedId}-title`;
  const descriptionId = `${generatedId}-description`;
  const errorId = `${generatedId}-error`;
  const firstNameId = `${generatedId}-first-name`;
  const lastNameId = `${generatedId}-last-name`;
  const dobId = `${generatedId}-dob`;
  const sexId = `${generatedId}-sex`;

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return;
    setOpen(nextOpen);
    if (!nextOpen) createPatient.reset();
  };

  const submitPatient = useCallback(
    async (values: CreatePatient) => {
      if (actor.type !== "physician") {
        const err = new Error("Only physicians can create patients.");
        captureException(err, {
          tags: { feature: "patients-v2", action: "create-patient" },
          extra: { actorType: actor.type },
        });
        throw err;
      }

      const data = {
        firstName: values.firstName,
        lastName: values.lastName,
        dob: NaiveDate.toString(values.dob),
        sex: values.sex,
        primaryCarePhysicianId: actor.user.id,
      } satisfies ApiCreatePatient;

      const result = await createPatient.mutateAsync({ data });

      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "/gw/stitch-web/api/patients",
      });

      const patientId = result.data.patient?.id;
      if (patientId == null) {
        toast.success("Patient created");
        return;
      }

      toast.success("Patient created", {
        action: {
          label: "View patient",
          onClick: () => {
            navigate(navigationV2.patientDetail(String(patientId)), {
              state: { source: "v2 patient list" },
            });
          },
        },
      });
    },
    [actor, createPatient, navigate, queryClient],
  );

  const handleSubmit = async (
    values: CreatePatientFormValues,
    helpers: FormikHelpers<CreatePatientFormValues>,
  ) => {
    try {
      await submitPatient(createPatientFromFormValues(values));
      helpers.resetForm();
      handleOpenChange(false);
    } catch {
      helpers.setSubmitting(false);
    }
  };

  if (!canCreatePatient) return null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        className="h-8 rounded-[9px] bg-surface text-[12.5px] font-semibold shadow-none"
        onClick={() => handleOpenChange(true)}
      >
        <Plus className="size-4" aria-hidden="true" />
        Create a patient
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          className="w-[min(calc(100vw-2rem),520px)] max-w-none rounded-[16px] border-primary/80 p-5 shadow-soft"
        >
          <Formik<CreatePatientFormValues>
            initialValues={CreatePatientFormInitialValues}
            validationSchema={CreatePatientFormValidationSchema}
            onSubmit={handleSubmit}
            validateOnBlur
            validateOnChange={false}
          >
            <CreatePatientDialogV2Form
              descriptionId={descriptionId}
              dobId={dobId}
              error={error}
              errorId={errorId}
              firstNameId={firstNameId}
              isSubmitting={isSubmitting}
              lastNameId={lastNameId}
              onOpenChange={handleOpenChange}
              sexId={sexId}
              titleId={titleId}
            />
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface CreatePatientDialogV2FormProps {
  descriptionId: string;
  dobId: string;
  error: string | null;
  errorId: string;
  firstNameId: string;
  isSubmitting: boolean;
  lastNameId: string;
  onOpenChange: (open: boolean) => void;
  sexId: string;
  titleId: string;
}

function CreatePatientDialogV2Form({
  descriptionId,
  dobId,
  error,
  errorId,
  firstNameId,
  isSubmitting,
  lastNameId,
  onOpenChange,
  sexId,
  titleId,
}: CreatePatientDialogV2FormProps) {
  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting: isFormikSubmitting,
    resetForm,
    setFieldValue,
    values,
  } = useFormikContext<CreatePatientFormValues>();
  const submitting = isSubmitting || isFormikSubmitting;

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  return (
    <form onSubmit={handleSubmit} className="grid gap-3" noValidate>
      <DialogHeader className="space-y-0 text-left">
        <DialogTitle
          id={titleId}
          className="font-mono text-[16px] font-semibold uppercase leading-none tracking-[0.08em] text-muted-foreground"
        >
          New patient
        </DialogTitle>
        <DialogDescription id={descriptionId} className="sr-only">
          Enter the patient&apos;s demographics to create their{" "}
          {PRODUCT_NAME.singular} record.
        </DialogDescription>
      </DialogHeader>

      {error ? (
        <Alert
          variant="destructive"
          aria-live="polite"
          aria-atomic="true"
          id={errorId}
        >
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-3 pt-1 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor={firstNameId} className="sr-only">
            First name
          </Label>
          <Input
            id={firstNameId}
            name="firstName"
            value={values.firstName}
            placeholder="First name"
            autoComplete="given-name"
            className="h-[46px] rounded-[12px] px-4 text-[16px] font-semibold text-surface-foreground placeholder:text-muted-foreground"
            aria-invalid={Boolean(errors.firstName)}
            aria-describedby={
              errors.firstName ? `${firstNameId}-error` : undefined
            }
            disabled={submitting}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          {errors.firstName ? (
            <p
              id={`${firstNameId}-error`}
              className="text-xs text-destructive"
              aria-live="polite"
            >
              {errors.firstName}
            </p>
          ) : null}
        </div>

        <div className="grid gap-2">
          <Label htmlFor={lastNameId} className="sr-only">
            Last name
          </Label>
          <Input
            id={lastNameId}
            name="lastName"
            value={values.lastName}
            placeholder="Last name"
            autoComplete="family-name"
            className="h-[46px] rounded-[12px] px-4 text-[16px] font-semibold text-surface-foreground placeholder:text-muted-foreground"
            aria-invalid={Boolean(errors.lastName)}
            aria-describedby={
              errors.lastName ? `${lastNameId}-error` : undefined
            }
            disabled={submitting}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          {errors.lastName ? (
            <p
              id={`${lastNameId}-error`}
              className="text-xs text-destructive"
              aria-live="polite"
            >
              {errors.lastName}
            </p>
          ) : null}
        </div>

        <div className="grid gap-2">
          <Label htmlFor={dobId} className="sr-only">
            Date of birth
          </Label>
          <Input
            id={dobId}
            name="dob"
            type="text"
            value={values.dob}
            placeholder="Date of birth"
            autoComplete="bday"
            inputMode="numeric"
            className="h-[46px] rounded-[12px] px-4 text-[16px] font-semibold text-surface-foreground placeholder:text-muted-foreground"
            aria-invalid={Boolean(errors.dob)}
            aria-describedby={errors.dob ? `${dobId}-error` : undefined}
            disabled={submitting}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          {errors.dob ? (
            <p
              id={`${dobId}-error`}
              className="text-xs text-destructive"
              aria-live="polite"
            >
              {errors.dob}
            </p>
          ) : null}
        </div>

        <div className="grid gap-2">
          <Label htmlFor={sexId} className="sr-only">
            Sex at birth
          </Label>
          <Select
            value={values.sex}
            disabled={submitting}
            onValueChange={(value) => setFieldValue("sex", value)}
          >
            <SelectTrigger
              id={sexId}
              className="h-[46px] rounded-[12px] px-4 text-[16px] font-semibold text-surface-foreground data-[placeholder]:text-muted-foreground [&>svg]:size-4"
              aria-invalid={Boolean(errors.sex)}
              aria-describedby={errors.sex ? `${sexId}-error` : undefined}
            >
              <SelectValue placeholder="Sex at Birth" />
            </SelectTrigger>
            <SelectContent>
              {sexOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.sex ? (
            <p
              id={`${sexId}-error`}
              className="text-xs text-destructive"
              aria-live="polite"
            >
              {errors.sex}
            </p>
          ) : null}
        </div>
      </div>

      <DialogFooter className="flex-row justify-end gap-3 pt-2 sm:space-x-0">
        <Button
          type="button"
          variant="outline"
          className="h-[38px] rounded-[12px] px-5 text-[15px] font-bold"
          disabled={submitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="h-[38px] rounded-[12px] px-5 text-[15px] font-bold"
          disabled={submitting}
        >
          {submitting ? <Spinner className="mr-1" /> : null}
          Create patient
        </Button>
      </DialogFooter>
    </form>
  );
}
